import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    User,
    Shield,
    Palette,
    LogOut,
    ChevronDown,
    Gift,
    Bell,
} from 'lucide-react'

import { useAuth } from '@/contexts/AuthContext'
import { useEntity } from '@/contexts/EntityContext'
import { useHoverDropdown } from '@/hooks/useHoverDropdown'
import { getInitials, getDisplayName } from '@/lib/user-utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface UserHeaderMenuProps {
    className?: string
}

/**
 * User menu for the header (top-right).
 * Shows avatar + name + chevron. Dropdown has same options as sidebar UserMenu.
 */
export function UserHeaderMenu({ className }: UserHeaderMenuProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { user, logout } = useAuth()
    const { currentEntity } = useEntity()
    const { open, onOpenChange, hoverProps, contentProps } = useHoverDropdown()

    const handleLogout = async () => {
        await logout()
        navigate('/login')
    }

    /**
     * Progressive fallback logic for role + entity display:
     * 1. "{Role} • {Entity}" if total ≤ 35 chars
     * 2. "{Role}" alone if role ≤ 30 chars
     * 3. "{Role truncated}..." with tooltip for full text
     * 
     * Priority for entity name: internalName (nom court) > entityName (raison sociale)
     */
    const getRoleEntityDisplay = (): { text: string; tooltip: string | null } => {
        const role = currentEntity?.roleName || t('user.myAccount')
        // Use internalName (short name) if available, otherwise entityName (full legal name)
        const entity = currentEntity?.internalName || currentEntity?.entityName || ''

        const MAX_FULL_LENGTH = 35
        const MAX_ROLE_LENGTH = 30
        const TRUNCATE_LENGTH = 27

        // Level 1: Full display "{Role} • {Entity}"
        const fullDisplay = entity ? `${role} • ${entity}` : role
        if (fullDisplay.length <= MAX_FULL_LENGTH) {
            return { text: fullDisplay, tooltip: null }
        }

        // Level 2: Role only (entity in tooltip)
        if (role.length <= MAX_ROLE_LENGTH) {
            return {
                text: role,
                tooltip: entity ? `${t('entity.currentEntity', 'Entité')} : ${entity}` : null
            }
        }

        // Level 3: Truncated role with tooltip
        const truncatedRole = role.substring(0, TRUNCATE_LENGTH) + '...'
        const fullTooltip = entity ? `${role}\n${t('entity.currentEntity', 'Entité')} : ${entity}` : role
        return { text: truncatedRole, tooltip: fullTooltip }
    }

    if (!user) return null

    const initials = getInitials(user.firstName, user.lastName, user.email)
    const displayName = getDisplayName(user, 20)
    const roleDisplay = getRoleEntityDisplay()

    return (
        <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
            <DropdownMenuTrigger
                {...hoverProps}
                className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-xl transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    className
                )}
            >
                <Avatar className="h-8 w-8 rounded-full">
                    {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={displayName} className="object-cover" />}
                    <AvatarFallback className="rounded-full bg-primary text-primary-foreground text-xs font-medium">
                        {initials}
                    </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-foreground hidden sm:inline">
                    {displayName}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:inline" />
            </DropdownMenuTrigger>

            <DropdownMenuContent
                {...contentProps}
                className="w-[--radix-dropdown-menu-trigger-width] min-w-64 rounded-xl"
                align="end"
                sideOffset={8}
            >
                <DropdownMenuLabel className="font-normal p-3 bg-muted/40 -mx-1 -mt-1 rounded-t-xl border-b mb-1">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 rounded-full">
                            {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={displayName} className="object-cover" />}
                            <AvatarFallback className="rounded-full bg-primary text-primary-foreground">
                                {initials}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col space-y-0.5 min-w-0 flex-1">
                            <p className="text-sm font-semibold text-foreground truncate">
                                {displayName}
                            </p>
                            {roleDisplay.tooltip ? (
                                <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {roleDisplay.text}
                                            </p>
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="bottom"
                                            className="max-w-xs whitespace-pre-line"
                                        >
                                            {roleDisplay.tooltip}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            ) : (
                                <p className="text-xs text-muted-foreground truncate">
                                    {roleDisplay.text}
                                </p>
                            )}
                        </div>
                    </div>
                </DropdownMenuLabel>

                <DropdownMenuGroup>
                    <DropdownMenuItem
                        onClick={() => navigate('/account/profile')}
                        className="rounded-lg mx-1 cursor-pointer"
                    >
                        <User className="mr-2 h-4 w-4" />
                        {t('user.profile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => navigate('/account/notifications')}
                        className="rounded-lg mx-1 cursor-pointer"
                    >
                        <Bell className="mr-2 h-4 w-4" />
                        {t('user.notifications')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => navigate('/account/security')}
                        className="rounded-lg mx-1 cursor-pointer"
                    >
                        <Shield className="mr-2 h-4 w-4" />
                        {t('user.security')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => navigate('/account/appearance')}
                        className="rounded-lg mx-1 cursor-pointer"
                    >
                        <Palette className="mr-2 h-4 w-4" />
                        {t('user.appearance')}
                    </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                    <DropdownMenuItem
                        onClick={() => navigate('/referral')}
                        className="rounded-lg mx-1 cursor-pointer"
                    >
                        <Gift className="mr-2 h-4 w-4 text-primary" />
                        <span className="text-primary font-medium">{t('user.referral')}</span>
                    </DropdownMenuItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    onClick={handleLogout}
                    className="rounded-lg mx-1 text-muted-foreground focus:text-red-600 focus:bg-destructive/10 cursor-pointer"
                >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('user.logout')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default UserHeaderMenu
